import { Box, Stack, Typography } from '@mui/material'
import { DeleteOutline, HideImageOutlined, LoopOutlined } from '@mui/icons-material'
import { FileAPIEntry, FileAction, FileState, UseFileAPIReturn } from 'components/common/FileAPI'
import { ReactNode, useMemo, useRef } from 'react'

import { CreativeUploadFolder } from './_main'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { GalleryImage } from 'components/common/Gallery/GalleryImage'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { supportedImageFileTypes } from 'constants/misc'
import { useTranslation } from 'react-i18next'

/** Interface describing config flags for ImageItem actions */
interface Config {
  /** Toggles availability of delete button */
  allowDelete: boolean
  /** Toggles availability of replace file button */
  allowReplace: boolean
  /** Toggles availability of retry button */
  allowRetry: boolean
}

/** Props for Image Item component */
interface Props<T extends { [key: string]: any }> {
  /** File to be displayed */
  file: FileAPIEntry<T>
  /** File controller instance */
  fileCtrl: UseFileAPIReturn
  /** On click of the entire image handler */
  onClick?: () => void
  /** Optional config as object with flags - can be partial  */
  config?: Partial<Config>
  /** Slot for extra button action to be displayed in top left corner */
  leftActionsSlot?: ReactNode
}

/**
 * Item used for representing visual file listed by FileAPI.  
 *   
 * Config flags allow to turn on/off delete, retry and replace functionality.  
 * Own actions can be added using *leftActionsSlot*  
 *   
 * All states and cases are handled using provided fileController instance.  
 *   
 * @example
 * <CreativeUploadImageItem<{ favorited: boolean }>
 *  file={fileEntry}
 *  fileCtrl={fileControllerInstance}
 * />
 */
export function CreativeUploadImageItem<T extends { [key: string]: any } = {}>({
  file,
  fileCtrl,
  leftActionsSlot,
  onClick,
  config = {},
}: Props<T>) {
  const { t } = useTranslation(['image_upload_item'])

  const inputRef = useRef<HTMLInputElement>(null)

  const normalizedConfig: Config = useMemo(() => ({
    allowDelete: false,
    allowRetry: false,
    allowReplace: false,
    ...config,
  }), [config])

  return (
    <>

      <Box display="none">
        <FileAPIUpload
          ref={inputRef}
          acceptedFileTypes={supportedImageFileTypes}
          uploadHandler={(acceptedFiles) => fileCtrl.reUploadFile(acceptedFiles[0], file.id, {
            tag: file.tag ?? CreativeUploadFolder.REQUIRED_VISUALS,
            onSuccess: () => {
              fileCtrl.loadFiles([file.id])
            },
          })}
          files={[]}
          showFileListing={false}
          maxNumberOfFiles={1}
        />
      </Box>

      <GalleryImage
        label={file.originalFilename ?? file.gcFilename ?? undefined}
        imageUrl={file.displayUrl ?? undefined}
        isLoading={file.state === FileState.RUNNING}
        isError={file.action !== FileAction.LOAD && file.state === FileState.ERROR}
        errorMessage={file.error ?? undefined}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
      >

        {file.action === FileAction.UPLOAD && file.state === FileState.RUNNING &&
          <MUIBadge
            sx={{
              position: 'absolute',
              top: '1rem',
              left: '1rem'
            }}
            label={`${file.progress}%`}
          />
        }

        {file.action === FileAction.LOAD && file.state === FileState.RUNNING &&
          <MUIBadge
            sx={{
              position: 'absolute',
              top: '1rem',
              left: '1rem'
            }}
            label={t('processing')}
          />
        }

        {!!leftActionsSlot &&
          <Stack
            sx={{
              position: 'absolute',
              top: '1rem',
              left: '1rem',
            }}
            direction="row"
            gap={1}
          >
            {leftActionsSlot}
          </Stack>
        }

        <Stack
          sx={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
          }}
          direction="row"
          gap={1}
        >

          {file.action === FileAction.LOAD && file.state !== FileState.RUNNING &&
            <>

              {normalizedConfig.allowReplace &&
                <MUITooltip content={t('replace')}>
                  <MUIButton
                    type="secondaryBorder"
                    size="sm"
                    isIconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      inputRef.current?.click()
                    }}
                  >
                    <LoopOutlined fontSize="large" />
                  </MUIButton>
                </MUITooltip>
              }
            </>
          }

          {normalizedConfig.allowDelete && file.state !== FileState.RUNNING &&
            <MUIButton
              type="orangeSecondaryBorder"
              size="sm"
              isIconButton
              onClick={(e) => {
                e.stopPropagation()
                fileCtrl.deleteFiles([file.id])
              }}
            >
              <DeleteOutline color="error" fontSize="large" />
            </MUIButton>
          }

          {normalizedConfig.allowRetry && (file.action === FileAction.UPLOAD || file.action === FileAction.RE_UPLOAD) && file.state === FileState.ERROR &&
            <MUITooltip content={t('retry')}>
              <MUIButton
                size="sm"
                isIconButton
                onClick={() => fileCtrl.reUploadFile(file.fileObject, file.id, {
                  tag: file.tag ?? CreativeUploadFolder.REQUIRED_VISUALS,
                  onSuccess: () => fileCtrl.loadFiles([file.id]),
                })}
              >
                <LoopOutlined fontSize="large" />
              </MUIButton>
            </MUITooltip>
          }

        </Stack>

        {file.action === FileAction.LOAD && file.state === FileState.ERROR &&
          <Stack gap={.5} alignItems="center">
            <HideImageOutlined fontSize="large" />
            <Typography variant="text-sm">
              {(t('no_thumbnail'))}
            </Typography>
          </Stack>
        }

      </GalleryImage>
    </>
  )
}
